import { ARCHITECTURE_FILTER_OPTIONS } from "../../../reservation-filters/architecture-filter-options";
import { CheckItem } from "../../ui/check-item";

import { EV_CHARGER_ICON, FACILITY_FILTERS } from "./facility-filter-options";

export type ConditionDraft = {
  architectureTypes: Set<string>;
  facilities: Set<string>;
  largeOnly: boolean;
  evCharger: boolean;
};

/**
 * 絞り込み条件フィルターのポップアップ内コンテンツ。
 * FilterPopupShell の children として渡す（area-filter.tsx と対称）。
 */
export function ConditionFilter({
  draft,
  onToggleArchType,
  onToggleFacility,
  onToggleLargeOnly,
  onToggleEvCharger,
}: {
  draft: ConditionDraft;
  onToggleArchType: (key: string) => void;
  onToggleFacility: (slug: string) => void;
  onToggleLargeOnly: () => void;
  onToggleEvCharger: () => void;
}) {
  return (
    <div className="flex flex-col gap-[10px] pb-[8px]">
      {/* アイコンのみ表示（テキストラベルなし、Figma 5179-11914）。アクセシブル名は aria-label に一本化 */}
      <div className="flex flex-col gap-[4px]">
        <p className="font-notojp text-dx-text-default text-[16px] leading-[28px] font-bold">
          設備
        </p>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(70px,1fr))]">
          {FACILITY_FILTERS.map((facility) => {
            const checked = draft.facilities.has(facility.slug);
            return (
              <CheckItem
                key={facility.slug}
                checked={checked}
                onChange={() => onToggleFacility(facility.slug)}
                ariaLabel={facility.label}
                className="w-full"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={facility.icon}
                  alt=""
                  className="size-[24px] shrink-0"
                />
              </CheckItem>
            );
          })}
          {/* EV充電器（設備セクションに統合） */}
          <CheckItem
            checked={draft.evCharger}
            onChange={onToggleEvCharger}
            ariaLabel="EV充電器"
            className="w-full"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={EV_CHARGER_ICON}
              alt=""
              className="size-[24px] shrink-0"
            />
          </CheckItem>
        </div>
      </div>

      <div className="flex flex-col gap-[4px]">
        <p className="font-notojp text-dx-text-default text-[16px] leading-[28px] font-bold">
          建築タイプ
        </p>
        {/* 2列グリッド */}
        <div className="flex flex-col">
          {/* ARCHITECTURE_FILTER_OPTIONS を2列に並べる（偶数インデックスが左列、奇数が右列） */}
          {chunk(ARCHITECTURE_FILTER_OPTIONS, 2).map((pair, rowIndex) => (
            <div key={rowIndex} className="flex gap-[8px]">
              {pair.map((option) => {
                const checked = draft.architectureTypes.has(option.value);
                return (
                  <CheckItem
                    key={option.value}
                    checked={checked}
                    onChange={() => onToggleArchType(option.value)}
                    className="flex-1"
                  >
                    <span className="font-notojp text-dx-text-default min-w-px flex-1 text-[14px] leading-[24px]">
                      {option.label}
                    </span>
                  </CheckItem>
                );
              })}
              {/* 奇数個の場合の余白補完 */}
              {pair.length === 1 && <div className="flex flex-1" />}
            </div>
          ))}
        </div>
      </div>

      <div className="border-dx-border-default border-t" />

      <CheckItem checked={draft.largeOnly} onChange={onToggleLargeOnly}>
        <span className="font-notojp text-dx-text-default text-[14px] leading-[24px]">
          Largeのみ表示
        </span>
      </CheckItem>
    </div>
  );
}

/** 配列を n 個ずつのチャンクに分割する純粋関数 */
function chunk<T>(arr: readonly T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size),
  );
}
